import styled from 'styled-components'
import { H4, Button, AddressCard, Pill, DrawerBodyWrapper, DrawerFooterWrapper, ItemCard } from '@base'
import { Spacings, Text } from '@styles'
import { getEnquiryInputType, showDateFormat } from '@utils'
import { enquiryInputType, enquiryStatus, strings } from '@constants'
import { paths } from '@routes'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { toggleDrawer, updateDrawerMeta } from '@data/state/action/root'
import { decimalSeparatedNumber } from '@utils'
import { useEffect } from 'react'

const SectionLabel = styled(H4)`
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.palette.text.tableHeader};
	display: flex;
	align-items: center;
`

const AddedItemsList = styled.div`
	padding: ${Spacings.SPACING_4B} 0;
`
const AddressCardWrapper = styled.div`
	margin-bottom: ${Spacings.SPACING_4B};
	border-radius: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_4B} 0;
`

const DateCardWrapper = styled.div`
	display: flex;
	justify-content: space-between;
`
const ExpiryDate = styled.span`
	display: inline-block;
	font-size: ${Text.MEDIUM};
	padding: ${Spacings.SPACING_4B} 0;
	font-weight: ${Text.BOLD_600};
`
const AddedItemsInfoWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
`

const ActionButton = styled(Button)`
	margin-left: ${Spacings.SPACING_4B};
`

const ViewEnquiry = ({ enquiry, role }) => {
	const navigate = useNavigate()
	const dispatch = useDispatch()

	const enquiryItems = enquiry?.enquiry_items ?? []

	const totalWeight =
		enquiryItems
			?.filter((item) => item?.input_type === enquiryInputType.WEIGHT || item?.input_type === null)
			?.reduce((acc, item) => acc + +item?.required_weight, 0) ?? 0

	const totalLength =
		enquiryItems?.filter((item) => item?.input_type === enquiryInputType.LENGTH)?.reduce((acc, item) => acc + +item?.required_weight, 0) ?? 0
	const totalSet =
		enquiryItems?.filter((item) => item?.input_type === enquiryInputType.SET)?.reduce((acc, item) => acc + +item?.required_weight, 0) ?? 0
	const totalPieces =
		enquiryItems?.filter((item) => item?.input_type === enquiryInputType.PIECES)?.reduce((acc, item) => acc + +item?.required_weight, 0) ?? 0

	const totalNumber =
		enquiryItems?.filter((item) => item?.input_type === enquiryInputType.NUMBER)?.reduce((acc, item) => acc + +item?.required_weight, 0) ?? 0
	const totalPower =
		enquiryItems?.filter((item) => item?.input_type === enquiryInputType.POWER)?.reduce((acc, item) => acc + +item?.required_weight, 0) ?? 0

	useEffect(() => {
		dispatch(
			updateDrawerMeta({
				label: (
					<>
						{strings('view_enquiry')} <Pill label={`${strings('enquiry_id')}:`} value={enquiry?.enquiry_id} margin={`0 0 0 auto`} />
					</>
				),
			})
		)
	}, [dispatch])

	const scrapPlatform = () => {
		const { final_quote_buyer } = enquiry
		const quotePerProduct = final_quote_buyer?.quote_per_product.find((item) => item?.for_price || item?.ex_price)
		return enquiry?.platform === 'SCRAP' && !!quotePerProduct
	}
	const editButtonHandler = () => {
		dispatch(toggleDrawer(false))
		if ([enquiryStatus.active, enquiryStatus.orderPlaced, enquiryStatus.expired].indexOf(enquiry.enquiry_status) > -1) {
			if (scrapPlatform()) {
				navigate(`${paths.manage_purchase_order_status('active')}?enquiry_id=${enquiry?.enquiry_id}`)
				return
			}
			if (enquiry.enquiry_status === enquiryStatus.active && !enquiry.final_quote_buyer) {
				navigate(paths.edit_enquiry(enquiry.enquiry_id), {
					state: enquiry,
				})
				return
			}
			navigate(paths.show_buyer_quotes(enquiry.enquiry_id), {
				state: enquiry,
			})
		} else {
			navigate(paths.edit_enquiry(enquiry.enquiry_id), {
				state: enquiry,
			})
		}
	}

	const closeClickHandler = () => {
		dispatch(toggleDrawer(false))
	}

	const createPoClickHandler = () => {
		navigate(paths.create_po(enquiry.enquiry_id), {
			state: enquiry,
		})
	}
	const getActionButtonText = () => {
		if (enquiry.enquiry_status === enquiryStatus.expired) return strings(['view', 'quotes'])
		if (enquiry.enquiry_status === enquiryStatus.orderPlaced || enquiry.final_quote_buyer) {
			if (scrapPlatform()) return strings(['previous', 'orders'])
			return strings(['see', 'quotes'])
		}
		return strings('edit')
	}

	const renderEnquiryItemsList = () => {
		return enquiryItems.map((item, index) => {
			const allotedLabel = getEnquiryInputType(item?.input_type)
			const itemData = [
				{
					label: strings('category'),
					value: item?.category_name,
				},
				{
					label: strings('product'),
					value: item?.product_name ?? '-',
					wrap: true,
				},
				{
					label: allotedLabel,
					value:
						item?.input_type === enquiryInputType.WEIGHT || item?.input_type === null
							? decimalSeparatedNumber((item?.required_weight / 1000).toFixed(3))
							: item?.required_weight,
				},
				{
					label: strings('brands'),
					value: item.brands?.join(', ') || item?.brand,
				},
				{
					label: strings('grade'),
					value: item?.grade ?? '-',
				},
				{
					label: strings('tc_reqd'),
					value: item?.tc_required ? strings('yes') : strings('no'),
				},
				{
					label: strings('message'),
					value: item?.message ?? '-',
					isMessage: true,
				},
			]
			return <ItemCard small data={itemData} id={item?.id} key={`enquiry-item-${index}`} />
		})
	}

	return (
		<>
			<DrawerBodyWrapper>
				<SectionLabel>
					<AddedItemsInfoWrapper>
						<Pill
							label={`${strings('total_items')}:`}
							value={enquiryItems?.length}
							margin={`0 ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} 0 `}
						/>

						{totalWeight > 0 && (
							<Pill
								label={`${strings('weight_mt')}:`}
								value={decimalSeparatedNumber(totalWeight / 1000, { default: 0 })}
								margin={`0 ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} 0 `}
							/>
						)}
						{totalLength > 0 && (
							<Pill label={`${strings('length')}:`} value={totalLength} margin={`0 ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} 0 `} />
						)}
						{totalSet > 0 && (
							<Pill label={`${strings('set')}:`} value={totalSet} margin={`0 ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} 0 `} />
						)}
						{totalPieces > 0 && (
							<Pill label={`${strings('pieces')}:`} value={totalPieces} margin={`0 ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} 0 `} />
						)}
						{totalNumber > 0 && (
							<Pill label={`${strings('number')}:`} value={totalNumber} margin={`0 ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} 0 `} />
						)}

						{totalPower > 0 && (
							<Pill label={`${strings('power')}:`} value={totalPower} margin={`0 ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} 0 `} />
						)}
					</AddedItemsInfoWrapper>
				</SectionLabel>
				<AddedItemsList>{renderEnquiryItemsList()}</AddedItemsList>
				<SectionLabel>{strings('shipping_address')}</SectionLabel>
				<AddressCardWrapper>
					<AddressCard data={enquiry?.shipping_address} />
				</AddressCardWrapper>
				<DateCardWrapper>
					<div>
						<SectionLabel>{strings('enquiry_date')}</SectionLabel>
						<ExpiryDate>{showDateFormat(new Date(enquiry?.enquiry_date))}</ExpiryDate>
					</div>
					<div>
						<SectionLabel>{strings('expiry_date')}</SectionLabel>
						<ExpiryDate>{showDateFormat(new Date(enquiry?.expiry_date))}</ExpiryDate>
					</div>
				</DateCardWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<ActionButton small uppercase type='primary' onClick={editButtonHandler}>
					{getActionButtonText()}
				</ActionButton>
				{enquiry?.platform === 'SCRAP' && enquiry?.enquiry_status !== enquiryStatus.created && (
					<ActionButton small uppercase type='secondary' onClick={createPoClickHandler}>
						{strings(['create', 'po'])}
					</ActionButton>
				)}
				<ActionButton small uppercase type='tertiary' onClick={closeClickHandler}>
					{strings('close')}
				</ActionButton>
			</DrawerFooterWrapper>
		</>
	)
}

export default ViewEnquiry
